/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Cognos Analytics Home (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'baglass/app/plugins/MenuActionInterface',
	'react',
	'react-dom',
	'../../nls/StringResources'
], (MenuActionInterface, React, ReactDOM, StringResources) => {

	class DetailsViewAction extends MenuActionInterface {

		constructor(context) {
			super();
			this.moduleId = 'ca-content/bridge/glass/Details';
			this.initialize(context);
		}

		initialize(context) {
			if (context) {
				this.glassContext = context.glassContext;
				this.eventRouter = this.glassContext.getCoreSvc('.Events');
			}
		}

		isItemVisible(context) {
			const selectedContext = this.getSelectedContext(context);
			if (!selectedContext) {
				return false;
			}
			const ancestors = selectedContext.ancestors;
			const lastAncestor = ancestors && ancestors[ancestors.length - 1];
			if (lastAncestor && lastAncestor.permissions) {
				return lastAncestor.permissions.includes('traverse');
			}
			return true;
		}

		_onDetailsSlideoutHide() {
			this.detailsSlideout = null;
			this.eventRouter.trigger('detailsView:update', { isOpen: false });
		}

		/**
		 * Open the Details Slideout
		 * @param {Object} context 
		 */
		openDetailsSlideout(context) {
			const selectedContext = this.getSelectedContext(context);
			const collectionItems = this.getCollectionItems(context);
			this.detailsSlideout = context.glassContext.currentAppView.openSlideout({
				'label': StringResources.get('details'),
				'position': 'right',
				'parent': null,
				'width': '320',
				'pinning': {
					'isPinned': true,
					'float': false,
					'display': false
				},
				'enableTabLooping': true,
				'onHide': this._onDetailsSlideoutHide.bind(this),
				'content': {
					'module': this.moduleId,
					'parentView': this,
					'selectedObject': selectedContext,
					'glassContext': context.glassContext,
					'collectionItems': collectionItems
				},
				'resizable': false
			});
			this.eventRouter.trigger('detailsView:update', { isOpen: true });
		}

		/**
		 * Closes the Details Slideout if it's open
		 * @param {Object} context 
		 */
		closeDetailsSlideout() {
			if (this.detailsSlideout) {
				this.detailsSlideout.hide({
					force: true,
					hideOnly: false
				});
			}
		}

		/**
		 * Returns true if the slideout is open
		 */
		isSlideoutOpen(context) {
			const slideouts = context.glassContext.currentAppView.getOpenSlideouts();
			this.detailsSlideout = slideouts.find(slideout => slideout.id === this.moduleId);
			return !!this.detailsSlideout;
		}

		/**
		 * Updates the details view with the provided context object
		 * @param {} context 
		 */
		updateDetailsSlideout(context, setFocus = false) {
			const selectedContext = this.getSelectedContext(context);
			if (this.isSlideoutOpen(context) &&
				this.detailsSlideout.contentView && this.detailsSlideout.contentView.updateSelection) {
				this.detailsSlideout.contentView.updateSelection(selectedContext, context.glassContext, setFocus);
			}
		}

		/** 
		 * This is called from the context menus and ContextToolbar
		 */
		onSelectItem(context) {
			let closeOnSelect = context.target.closeOnSelect === true;
			if (!this.isSlideoutOpen(context)) {
				this.openDetailsSlideout(context);
			} else if (closeOnSelect) {
				//this is coming from the context toolbar so close
				this.closeDetailsSlideout(context);
			} else {
				this.updateDetailsSlideout(context, true);
			}
		}

		/**
		 * Returns the context using the current targetSelectedCount
		 * @param {*} context 
		 */
		getSelectedContext(context) {
			const activeObject = context && context.target && context.target.activeObject;
			const object = activeObject && (activeObject.targetSelectedContext || activeObject.aSelectedContext);
			if (Array.isArray(object)) {
				if (object.length === 1) {
					return object[0];
				} else {
					return null;
				}
			}

			return context;
		}

		getCollectionItems(context) {
			const activeObject = context && context.target && context.target.activeObject;
			const collectionItems =  activeObject && activeObject.collectionItems;
			return collectionItems || [];
		}

	}

	return DetailsViewAction;
});